import type { MobileRoute } from '@lumapps/mobile-webview/message-emit/redirect/types';
import { createPageRoute, Route } from '@lumapps/router';
import { AppId } from '@lumapps/router/constants';

import { FILTER_TRAINING_TYPE } from './constants';
import { TRAINING_TYPE, ACTIVITY_TYPES, TRAINING_COURSE_VIEWS, TRAINING_LEGACY_DATA_TYPE } from './types';

/**
 * Parameters for redirecting to a training
 */
export interface TrainingRouteParams {
    /**
     * The training id to redirect to.
     */
    id: string;
    /**
     * The training type.
     */
    type?: TRAINING_TYPE | TRAINING_LEGACY_DATA_TYPE;
    /**
     * The view to redirect to.
     */
    view?: TRAINING_COURSE_VIEWS;
    /**
     * The identifier of the view type
     */
    identifier?: string;
    /**
     * The activity type
     */
    activityType?: ACTIVITY_TYPES;
    /**
     * The duel id
     */
    duelId?: string;
}

export const TRAINING_LEGACY_PATH = 'learning/training/:id';
export const SCORM_TRAINING_PATH = 'scorm-trainings/:id';
export const LEARNING_PATH_PATH = 'learning-paths/:id';
export const RECOMMENDATION_QUIZ_PATH = 'recommendation-quizzes/:id';
export const LEARNING_PATH = 'learning';
export const LEARNING_MANAGER_PATH = 'learning-manager';

const TRAINING_COURSE_BASE_URL = 'training-courses/:id';
export const TRAINING_COURSE_PATH = `${TRAINING_COURSE_BASE_URL}/:view(${Object.values(TRAINING_COURSE_VIEWS).join(
    '|',
)})?/:identifier?`;

const createViewPath = (view: TRAINING_COURSE_VIEWS, withIdentifier = false) =>
    `${TRAINING_COURSE_BASE_URL}/:view(${view})${withIdentifier ? '/:identifier' : ''}`;

export const TRAINING_COURSE_VIEW_ACTIVITIES_PATH = createViewPath(TRAINING_COURSE_VIEWS.ACTIVITIES);
export const TRAINING_COURSE_VIEW_ACTIVITY_PATH = createViewPath(TRAINING_COURSE_VIEWS.ACTIVITIES, true);
export const TRAINING_COURSE_VIEW_TOOLBOXES_PATH = createViewPath(TRAINING_COURSE_VIEWS.TOOLBOXES);
export const TRAINING_COURSE_VIEW_TOOLBOX_PATH = createViewPath(TRAINING_COURSE_VIEWS.TOOLBOXES, true);
export const TRAINING_COURSE_VIEW_LEADERBOARD_PATH = createViewPath(TRAINING_COURSE_VIEWS.LEADERBOARD);
export const TRAINING_COURSE_VIEW_ACHIEVEMENTS_PATH = createViewPath(TRAINING_COURSE_VIEWS.ACHIEVEMENTS);

export const trainingCourseRoute: Route = createPageRoute({
    slug: TRAINING_COURSE_PATH,
    appId: AppId.frontOffice,
});

export const trainingCourseActivitiesRoute: Route = createPageRoute({
    slug: TRAINING_COURSE_VIEW_ACTIVITIES_PATH,
    appId: AppId.frontOffice,
});

export const trainingCourseActivityRoute: Route = createPageRoute({
    slug: TRAINING_COURSE_VIEW_ACTIVITY_PATH,
    appId: AppId.frontOffice,
});

export const trainingCourseToolboxesRoute: Route = createPageRoute({
    slug: TRAINING_COURSE_VIEW_TOOLBOXES_PATH,
    appId: AppId.frontOffice,
});

export const trainingCourseToolboxRoute: Route = createPageRoute({
    slug: TRAINING_COURSE_VIEW_TOOLBOX_PATH,
    appId: AppId.frontOffice,
});

export const trainingCourseLeaderboardRoute: Route = createPageRoute({
    slug: TRAINING_COURSE_VIEW_LEADERBOARD_PATH,
    appId: AppId.frontOffice,
});

export const trainingCourseAchievementsRoute: Route = createPageRoute({
    slug: TRAINING_COURSE_VIEW_ACHIEVEMENTS_PATH,
    appId: AppId.frontOffice,
});

export const learningPathRoute: Route = createPageRoute({
    slug: LEARNING_PATH_PATH,
    appId: AppId.frontOffice,
});

export const scormTrainingRoute: Route = createPageRoute({
    slug: SCORM_TRAINING_PATH,
    appId: AppId.frontOffice,
});

export const recommendationQuizRoute: Route = createPageRoute({
    slug: RECOMMENDATION_QUIZ_PATH,
    appId: AppId.frontOffice,
});

export const trainingLegacyRoute: Route = createPageRoute({
    slug: TRAINING_LEGACY_PATH,
    appId: AppId.frontOffice,
});

export const learningRoute: Route = createPageRoute({
    slug: LEARNING_PATH,

    appId: AppId.frontOffice,
});

export const learningManagerRoute: Route = createPageRoute({
    slug: LEARNING_MANAGER_PATH,
    appId: AppId.frontOffice,
});

export const getTrainingCourseActivitiesRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...trainingCourseActivitiesRoute,
    params: { id, view: TRAINING_COURSE_VIEWS.ACTIVITIES },
    mobilePageDescriptor: { type: 'training-course', id, view: TRAINING_COURSE_VIEWS.ACTIVITIES },
});

export const getTrainingCourseActivityRoute = ({
    id,
    identifier,
    activityType,
    duelId,
}: TrainingRouteParams): MobileRoute => ({
    ...trainingCourseActivityRoute,
    params: { id, identifier, view: TRAINING_COURSE_VIEWS.ACTIVITIES },
    query: { activityType, duelId },
    mobilePageDescriptor: { type: 'activity', id: identifier!, trainingId: id, activityType: activityType! },
});

export const getTrainingCourseToolboxesRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...trainingCourseToolboxesRoute,
    params: { id, view: TRAINING_COURSE_VIEWS.TOOLBOXES },
    mobilePageDescriptor: { type: 'training-course', id, view: TRAINING_COURSE_VIEWS.TOOLBOXES },
});

export const getTrainingCourseToolboxRoute = ({ id, identifier }: TrainingRouteParams): MobileRoute => ({
    ...trainingCourseToolboxRoute,
    params: { id, identifier, view: TRAINING_COURSE_VIEWS.TOOLBOXES },
    mobilePageDescriptor: { type: 'toolbox', id: identifier!, trainingId: id },
});

export const getTrainingCourseLeaderboardRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...trainingCourseLeaderboardRoute,
    params: { id, view: TRAINING_COURSE_VIEWS.LEADERBOARD },
    mobilePageDescriptor: { type: 'training-course', id, view: TRAINING_COURSE_VIEWS.LEADERBOARD },
});

export const getTrainingCourseAchievementsRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...trainingCourseAchievementsRoute,
    params: { id, view: TRAINING_COURSE_VIEWS.ACHIEVEMENTS },
    mobilePageDescriptor: { type: 'training-course', id, view: TRAINING_COURSE_VIEWS.ACHIEVEMENTS },
});

/** Get the training course route. */
export const getTrainingCourseRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...trainingCourseRoute,
    params: { id },
    mobilePageDescriptor: { type: 'training-course', id },
});

/** Get the learning path route. */
export const getLearningPathRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...learningPathRoute,
    params: { id },
    mobilePageDescriptor: { type: 'learning-path', id },
});

/** Get the training scorm route. */
export const getScormTrainingRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...scormTrainingRoute,
    params: { id },
    mobilePageDescriptor: { type: 'scorm-training', id },
});

/** Get the recommendation quiz route. */
export const getRecommendationQuizRoute = ({ id }: TrainingRouteParams): MobileRoute => ({
    ...recommendationQuizRoute,
    params: { id },
    mobilePageDescriptor: { type: 'recommendation-quiz', id },
});

/**
 * Training entry point.
 *
 * @param type the training type
 * @param id the training id
 */
export const getTrainingRoute = ({ type = TRAINING_TYPE.TRAINING, id = '' }: TrainingRouteParams) => {
    switch (FILTER_TRAINING_TYPE[type]) {
        case TRAINING_TYPE.TRAINING:
            return getTrainingCourseRoute({ id });
        case TRAINING_TYPE.SCORM_TRAINING:
            return getScormTrainingRoute({ id });
        case TRAINING_TYPE.LEARNING_PATH:
            return getLearningPathRoute({ id });
        case TRAINING_TYPE.RECOMMENDATION_QUIZ:
            return getRecommendationQuizRoute({ id });
        default:
            return getTrainingCourseRoute({ id });
    }
};

export const getLearningRoute = (contentType: string): MobileRoute => ({
    ...learningRoute,
    params: {
        type: contentType,
    },

    mobilePageDescriptor: {
        type: 'learning',
        id: contentType,
    },
});

export const getLearningManagerRoute = (contentType: string): MobileRoute => ({
    ...learningManagerRoute,
    params: {
        type: contentType,
    },

    mobilePageDescriptor: {
        type: 'learning-manager',
        id: contentType,
    },
});
